import { sync_info } from "../actions/syncInfo";
import { router } from "../router";
import { LocalStorageUser } from "../states/auth";
import { domains } from "../states/domains";
import { goFinish } from "../states/finishState";
import { apiDisconnected } from "../states/media/api";
import { EventEmmiter } from "../utils/EventEmitter";

const reconnectTimeout = 1000 * 5;

class WSInstance extends EventEmmiter {
  connected = false;
  ws?: WebSocket;
  buffer: any[] = [];
  recorder = false;
  need_reconnect = true;
  attempt = 0;
  last_activity = Date.now();
  pingInterval: any;
  checkInterval: any;
  private onopen: (e: Event) => void = () => { };
  private onclose = () => { };
  private onmessage: (ev: MessageEvent<any>) => void = () => { };
  constructor() {
    super();
  }

  async connect(roomId: string, recorder = false) {
    this.recorder = recorder;
    return new Promise<void>((resolve, reject) => {
      const local = localStorage.getItem("user");
      if (local) {
        const user = JSON.parse(local) as LocalStorageUser;
        const token = user.token;
        this.ws = new WebSocket(
          `wss://${domains.main_host
          }/ws?token=${token}&room=${roomId}`,
        );
        this.onopen = async () => {
          if (this.attempt !== 0) {
            if(!this.connected) await this.wait_connected()
            sync_info()
          }
          this.attempt = 0;
          this.last_activity = Date.now();
          this.setupPing();
          if (this.ws) this.ws.onopen = null;
          if (this.buffer.length) {
            while (this.buffer.length > 0) {
              const data = this.buffer.shift();
              this.ws?.send(JSON.stringify(data));
            }
          }
          resolve();
        };
        this.ws.onopen = this.onopen;
        this.onclose = () => {
          apiDisconnected()
          if (this.ws) {
            this.ws.onmessage = null;
            this.ws.onclose = null;
            this.ws.onopen = null;
            this.ws.onerror = null;
          }
          this.ws = undefined;
          this.connected = false
          this.disablePing();
          if (this.need_reconnect) {
            this.attempt++;
            if (this.attempt < 10) {
              setTimeout(() => {
                console.log("reconnect ws attempt", this.attempt);
                this.connect(roomId, recorder);
              }, reconnectTimeout);
            } else {
              this.need_reconnect = false;
              goFinish("network");
            }
          }
        };
        this.ws.onclose = this.onclose;
        this.onmessage = (ev) => {
          this.last_activity = Date.now();
          if (ev.data) {
            try {
              const parsed = JSON.parse(ev.data);
              if (parsed.event) {
                const is_system = this.prepare_system_event(parsed);
                if (!is_system) this.emit(parsed.event, parsed.data);
              } else {
                console.log("incorrect message", parsed);
              }
            } catch {
              console.log("unknown message", ev.data);
            }
          }
        };
        this.ws.onmessage = this.onmessage;
      } else {
        reject("missing token");
      }
    });
  }

  setupPing() {
    this.pingInterval = setInterval(() => {
      this.message("ping", {});
    }, 1000 * 10);
    this.checkInterval = setInterval(() => {
      if (Date.now() - this.last_activity > 15 * 1000) {
        console.log("ws timeout err");
        this.onclose();
      }
    }, 1000 * 5);
  }

  disablePing() {
    if (this.pingInterval) clearInterval(this.pingInterval);
    if (this.checkInterval) clearInterval(this.checkInterval);
  }

  disconnect() {
    this.need_reconnect = false;
    this.ws?.close();
  }

  message(event: string, data: any) {
    const to_send = {
      event: event,
      data: data
    }
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(
        JSON.stringify(to_send),
      );
    } else {
      this.buffer.push(to_send);
    }
  }

  api_message(event: string, data: any) {
    const to_send = {
      event: "api_message",
      data: {
        event,
        data
      }
    }
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(
        JSON.stringify(to_send),
      );
    } else {
      this.buffer.push(to_send);
    }
  }

  prepare_system_event(data: { event: string; data: any }) {
    if (this.recorder) return false;
    if(data.event === "ws-connected") {
      this.connected = true;
      this.emit('ws-connected')
    } else if (data.event === "already-connected") {
      this.need_reconnect = false;
      router.push({
        name: "error",
        params: {
          errtype: data.event,
        },
      });
      return true;
    } else if (data.event === "bad-query") {
      this.need_reconnect = false;
      router.push({
        name: "error",
        params: {
          errtype: data.event,
        },
      });
      return true;
    } else if (data.event === "bad-user") {
      this.need_reconnect = false;
      router.push({
        name: "error",
        params: {
          errtype: data.event,
        },
      });
      return true;
    } else if (data.event === "check-connect") {
      this.message("ping", {});
      return true;
    } else if(data.event === "new-auth") {
      this.need_reconnect = false;
      goFinish('new-auth')
      return true
    }
    return false;
  }

  async wait_connected() {
    return new Promise(resolve => {
      this.once('ws-connected', resolve)
    })
  }
}

export const ws_instance = new WSInstance();
